import React, { useMemo, useState, useEffect } from "react";
import Button from "@mui/material/Button";
import { ConfirmDialog, TablePage } from "@outpave/ds";
import { useTranslation } from "react-i18next";
import useCompanySettings from "../../hooks/useCompanySettings";
import ProfileSettings, {
  profileSkeletonRows,
} from "components/admin/settings/CompanySettings/ProfileSettings";
import GeneralSettings, {
  GENERAL_SKELETON_ROWS,
} from "components/admin/settings/CompanySettings/GeneralSettings";
import { buildCompanySettings } from "validations/companySettings";
import { useFeatureEntitlements } from "@frontegg/react";
import { Box, Stack, Typography } from "@mui/material";
import InvoiceSettings, {
  INVOICE_SKELETON_ROWS,
} from "components/admin/settings/CompanySettings/InvoiceSettings";
import { SettingsRowsSkeleton } from "components/admin/settings/CompanySettings/SettingsRow";

import { useLocation } from "react-router-dom";
import { useNavBreadcrumbs } from "hooks/useNavBreadcrumbs";

const CompanySettings: React.FC = () => {
  const { t } = useTranslation();
  const breadcrumbs = useNavBreadcrumbs(t("Company Settings"));
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const startInEditMode = params.get("editMode") === "true";
  const [activeTab, setActiveTab] = useState(0);
  const [editMode, setEditMode] = useState(startInEditMode);
  const [isShowConfirmModal, setIsShowConfirmModal] = useState(false);
  const [pendingTab, setPendingTab] = useState<number | null>(null);

  const { isEntitled: featureInvoices } =
    useFeatureEntitlements("feature_invoices");
  const { isEntitled: featurePayments } =
    useFeatureEntitlements("feature_payments");

  const skeletonRows: Record<string, boolean[]> = {
    profile: profileSkeletonRows(featurePayments),
    invoice: INVOICE_SKELETON_ROWS,
    general: GENERAL_SKELETON_ROWS,
  };

  // Without the payments entitlement the profile tab hides the payment email,
  // so requiring it would disable Save with nothing on screen to explain it.
  const profileValidation = useMemo(
    () => buildCompanySettings({ requirePaymentEmail: featurePayments }),
    [featurePayments],
  );

  const handleSaveError = () => {
    setEditMode(true);
  };

  const {
    companyProfile,
    handleSubmitCompanyProfile,
    invoiceSettings,
    handleSubmitInvoiceSettings,
    generalSettings,
    defaultMileageData,
    handleSubmitGeneralSettings,
    specificLoadingStates,
    specificUploadingStates,
    paymentEmailDomain,
  } = useCompanySettings({
    formikValidation: profileValidation,
    onSaveError: handleSaveError,
  });

  const handleCancel = () => {
    companyProfile.resetForm();
    generalSettings.resetForm();
    invoiceSettings.resetForm();
    setEditMode(!editMode);
  };

  const handleTabChange = (newTab: number) => {
    if (editMode) {
      setPendingTab(newTab);
      setIsShowConfirmModal(true);
    } else {
      setActiveTab(newTab);
    }
  };

  const handleConfirmChangeTab = () => {
    if (pendingTab !== null) {
      setActiveTab(pendingTab);
      setPendingTab(null);
      handleCancel();
    }
    setIsShowConfirmModal(false);
  };

  const handleCloseConfirmModal = () => {
    setPendingTab(null);
    setIsShowConfirmModal(false);
  };

  type SectionAction = {
    onClick: () => void;
    title: string;
    disabled?: boolean;
  };

  const sections: {
    id: string;
    label: string;
    description?: string;
    action?: SectionAction;
    subAction?: SectionAction;
    isVisible?: boolean;
  }[] = [
    {
      id: "profile",
      label: "Company Profile",
      isVisible: true,
      action: editMode
        ? {
            onClick: async () => {
              await handleSubmitCompanyProfile();
              setEditMode(false);
            },
            title: t("Save"),
            disabled:
              companyProfile.isSubmitting ||
              !companyProfile.isValid ||
              !companyProfile.dirty,
          }
        : {
            onClick: () => setEditMode(true),
            title: t("Edit"),
            disabled: false,
          },
      subAction: editMode
        ? {
            onClick: handleCancel,
            title: t("Cancel"),
          }
        : undefined,
    },
    {
      id: "invoice",
      label: "Invoice Settings",
      isVisible: featureInvoices,
      action: editMode
        ? {
            onClick: async () => {
              await handleSubmitInvoiceSettings();
              setEditMode(false);
            },
            title: t("Save"),
            disabled: invoiceSettings.isSubmitting || !invoiceSettings.dirty,
          }
        : {
            onClick: () => setEditMode(true),
            title: t("Edit"),
            disabled: false,
          },
      subAction: editMode
        ? {
            onClick: handleCancel,
            title: t("Cancel"),
          }
        : undefined,
    },
    {
      id: "general",
      label: "General",
      isVisible: true,
      action: editMode
        ? {
            onClick: async () => {
              await handleSubmitGeneralSettings();
              setEditMode(false);
            },
            title: t("Save"),
            disabled:
              generalSettings.isSubmitting ||
              !generalSettings.isValid ||
              !generalSettings.dirty,
          }
        : {
            onClick: () => setEditMode(true),
            title: t("Edit"),
            disabled: false,
          },
      subAction: editMode
        ? {
            onClick: handleCancel,
            title: t("Cancel"),
          }
        : undefined,
    },
  ];

  // The old shell did this: an activeTab pointing at a hidden entry would
  // otherwise render that section with no tab selected. Sets the tab directly
  // rather than going through handleTabChange, which would prompt about unsaved
  // edits for a move the user did not make.
  const activeSectionHidden = sections[activeTab]?.isVisible === false;
  const firstVisibleSection = sections.findIndex(
    (tab) => tab.isVisible !== false,
  );

  useEffect(() => {
    if (activeSectionHidden) setActiveTab(firstVisibleSection);
  }, [activeSectionHidden, firstVisibleSection]);

  const activeSection = sections[activeTab];
  const activeSectionId = activeSection.id;

  return (
    <>
      <TablePage
        title={t("Company Settings")}
        breadcrumbs={breadcrumbs}
        primaryAction={
          activeSection.action && (
            <Button
              onClick={activeSection.action.onClick}
              disabled={activeSection.action.disabled}
            >
              {activeSection.action.title}
            </Button>
          )
        }
        secondaryAction={
          activeSection.subAction && (
            <Button
              variant="primary-outlined"
              onClick={activeSection.subAction.onClick}
              disabled={activeSection.subAction.disabled}
            >
              {activeSection.subAction.title}
            </Button>
          )
        }
        tabs={{
          items: sections
            .map((tab, index) => ({ tab, index }))
            .filter(({ tab }) => tab.isVisible !== false)
            .map(({ tab, index }) => ({
              id: String(index),
              label: t(tab.label),
            })),
          activeId: String(activeTab),
          onChange: (id) => handleTabChange(Number(id)),
        }}
      >
        <Stack sx={{ overflow: "auto" }}>
          {activeSection.description && (
            <Typography variant="body2">
              {t(activeSection.description)}
            </Typography>
          )}
          {specificLoadingStates[activeSectionId] ? (
            <SettingsRowsSkeleton
              rows={skeletonRows[activeSectionId]}
              label={t("Loading")}
            />
          ) : (
            <Box>
              {activeSectionId === "profile" && (
                <ProfileSettings
                  editMode={editMode}
                  profileFormik={companyProfile}
                  uploading={specificUploadingStates[activeSectionId]}
                  paymentEmailDomain={paymentEmailDomain}
                />
              )}

              {activeSectionId === "invoice" && (
                <InvoiceSettings
                  editMode={editMode}
                  invoiceFormik={invoiceSettings}
                  uploading={specificUploadingStates[activeSectionId]}
                />
              )}

              {activeSectionId === "general" && (
                <GeneralSettings
                  generalFormik={generalSettings}
                  editMode={editMode}
                  defaultMileageData={defaultMileageData}
                />
              )}
            </Box>
          )}
        </Stack>
      </TablePage>
      <ConfirmDialog
        open={isShowConfirmModal}
        tone="primary"
        title={t("Unsaved Changes")}
        message={t("UnsavedTabChanges")}
        confirmLabel={t("Yes")}
        cancelLabel={t("No")}
        onConfirm={handleConfirmChangeTab}
        onCancel={handleCloseConfirmModal}
      />
    </>
  );
};

export default CompanySettings;
